import {
  AnimationMixer,
  Group,
  LoopOnce,
  LoopRepeat,
  type AnimationAction,
  type AnimationClip,
  type Object3D,
} from 'three'
import type { CharacterVisualState } from './SceneBridge'

/**
 * An imported model driven by the same state the procedural rig reads.
 *
 * The rig and this class are interchangeable from the game's point of view:
 * both take a `CharacterVisualState` and a delta, and neither is allowed to
 * touch the simulation. The difference is where the pose comes from — code, or
 * an animator's clips.
 *
 * Clip names are matched loosely and case-insensitively, because every pack
 * names them differently: "Idle", "idle", "Armature|Idle", "CharacterArmature|Idle".
 * A model missing a clip simply keeps whatever it was doing, rather than
 * throwing — a fighter animating a bit wrong is playable, a crash is not.
 */
export class ModelFighter {
  readonly group = new Group()

  private readonly mixer: AnimationMixer
  private readonly actions = new Map<string, AnimationAction>()
  private current: AnimationAction | null = null
  private currentName = ''
  /** Set while a one-shot (a hit reaction, a fall) owns the body. */
  private oneShot = 0
  private lastBounces = 0
  private wasAirborne = false

  constructor(model: Object3D, clips: AnimationClip[]) {
    this.group.add(model)
    this.mixer = new AnimationMixer(model)
    for (const clip of clips) {
      this.actions.set(normalise(clip.name), this.mixer.clipAction(clip))
    }
  }

  /**
   * Picks a clip from the fighter's state and crossfades to it.
   *
   * The priority order is the order the player reads events in: being thrown
   * beats casting, casting beats running, running beats standing.
   */
  update(state: CharacterVisualState, dt: number): void {
    this.oneShot = Math.max(0, this.oneShot - dt)

    // Landing from a launch: play the impact once, then let the loop resume.
    if (state.bounces > this.lastBounces && this.play(['hitreact', 'hitrecieve', 'death'], true)) {
      this.oneShot = 0.45
    }
    this.lastBounces = state.bounces

    if (state.airborne || state.falling) {
      this.wasAirborne = true
      // The roster's 24 clips have no jump: they are ground-fighting sets. The
      // second hit reaction is the closest thing to a body in the air, and it
      // matters that `death` stays last — reaching it first made every fighter
      // who was merely knocked back play their own death on the way.
      this.play(['jump', 'walkjump', 'hitrecieve2', 'death'], false)
    } else {
      if (this.wasAirborne) this.oneShot = 0
      this.wasAirborne = false
      if (this.oneShot <= 0) {
        if (state.castPhase !== null) this.play(['punch', 'kick', 'wave'], false)
        else if (state.bracing) this.play(['sitting', 'no', 'idle'], false)
        else if (state.speed > 5) this.play(['running', 'run', 'walking'], false)
        else if (state.speed > 0.6) this.play(['walking', 'walk', 'idle'], false)
        else this.play(['idle', 'standing'], false)
      }
    }

    // Clips are authored at a natural pace; scale the loop with actual speed so
    // the feet keep up with the ground the fighter is covering.
    if (this.current && !this.oneShot) {
      this.mixer.timeScale = state.speed > 0.6 ? clampRate(state.speed / 6) : 1
    } else {
      this.mixer.timeScale = 1
    }
    this.mixer.update(dt)
  }

  /** Crossfades to the first name that exists. Returns whether it switched. */
  private play(names: string[], once: boolean): boolean {
    for (const raw of names) {
      const name = normalise(raw)
      const action = this.find(name)
      if (!action) continue
      if (this.currentName === name) return false

      action.reset()
      action.setLoop(once ? LoopOnce : LoopRepeat, once ? 1 : Infinity)
      action.clampWhenFinished = once
      action.enabled = true
      action.setEffectiveWeight(1)
      if (this.current) action.crossFadeFrom(this.current, 0.18, false)
      action.play()

      this.current = action
      this.currentName = name
      return true
    }
    return false
  }

  /** Exact match first, then any clip whose name contains the wanted word. */
  private find(name: string): AnimationAction | undefined {
    const exact = this.actions.get(name)
    if (exact) return exact
    for (const [key, action] of this.actions) {
      if (key.includes(name)) return action
    }
    return undefined
  }

  dispose(): void {
    this.mixer.stopAllAction()
  }
}

function normalise(name: string): string {
  // "CharacterArmature|Idle" → "idle"
  const tail = name.includes('|') ? name.slice(name.lastIndexOf('|') + 1) : name
  return tail.trim().toLowerCase().replace(/[\s_-]/g, '')
}

function clampRate(rate: number): number {
  return rate < 0.6 ? 0.6 : rate > 1.8 ? 1.8 : rate
}
